/**
 * Procedural ground palettes (2026-09-14, q-client-terrain-consumer-build) — the
 * PURE, node-testable half of the procedural ground material. Like plan.ts it
 * imports NO `three` and does no I/O; `groundMaterial.ts` is the thin GLSL half
 * that turns one of these into a patched `MeshStandardMaterial`.
 *
 * Why it exists: the ground was one flat colour (`#1d2c42`) with a grid, so a
 * capture could not show scale, and an animated creature sliding its feet was
 * invisible against it. Until authored ground textures exist (TGC-ENV-049 is
 * undelivered and `content/themes/` is empty) the ground is shaded procedurally
 * from world-space noise — 0 MB of textures, deterministic, no time uniform.
 *
 * Palette choice (docs/art/art-direction-bible.md §3.3 master anchors + §11 biome
 * palettes): the default is a muted grass/soil/dry-grass mix pushed off the
 * Bible's foliage `#5f8f4e` and timber `#7c5a3a` anchors — calm, natural, and
 * deliberately DARKER and less saturated than the anchors so props and creatures
 * stand off it. Every colour is kept well away from the §3.7 reserved hues
 * (hostile telegraph `#e8443a`, safe/heal `#39c46a`) — pinned by groundMaterial.test.ts.
 *
 * Plan key: `GroundPlan.palette` is OPTIONAL and set only when the joined biome
 * has an entry in {@link BIOME_GROUND_PALETTE}; absent ⇒ the default palette. So
 * the legacy placeholder plan (and every test that pins it) is byte-identical.
 *
 * Import boundary (AD-122): frontend-local; no shared/server imports.
 */

/** One ground palette: three albedo stops, a roughness band and the noise scales. */
export interface GroundPalette {
  readonly id: string;
  /** Dominant albedo (sRGB hex) — the macro field's low end (e.g. grass). */
  readonly base: string;
  /** Second albedo (sRGB hex) — the macro field's high end (e.g. bare soil). */
  readonly patch: string;
  /** Accent albedo (sRGB hex) — mid-scale speckled patches (e.g. dry grass, grit). */
  readonly accent: string;
  /** Roughness range [min, max] the noise varies across. */
  readonly roughness: readonly [number, number];
  /** Macro patch size in metres (base ↔ patch blend). */
  readonly macroMetres: number;
  /** Accent patch size in metres. */
  readonly accentMetres: number;
  /** Fine grain cell in metres — the scale/motion reference a video reads. */
  readonly grainMetres: number;
  /** Grain luminance amplitude (±fraction of albedo). */
  readonly grainAmount: number;
}

const COMMON_SCALES = {
  macroMetres: 14,
  accentMetres: 3.5,
  grainMetres: 0.45,
  grainAmount: 0.16,
} as const;

/** Default id — used whenever a plan carries no `palette` key. */
export const DEFAULT_GROUND_PALETTE_ID = 'earth-meadow';

export const GROUND_PALETTES: Readonly<Record<string, GroundPalette>> = {
  // Default: muted grass over soil, dry-grass flecks. Natural, calm, readable.
  'earth-meadow': {
    id: 'earth-meadow',
    base: '#4b6a3c',
    patch: '#6b5439',
    accent: '#857b4e',
    roughness: [0.82, 1],
    ...COMMON_SCALES,
  },
  // Verdant Reach (Tier 1 forest): fresher greens, moss, a little leaf litter.
  'verdant-forest': {
    id: 'verdant-forest',
    base: '#4a6f3b',
    patch: '#5a4a33',
    accent: '#6f8546',
    roughness: [0.8, 1],
    ...COMMON_SCALES,
  },
  // Starlight Meadows (Tier 1 meadow): brighter, drier open grass.
  'open-meadow': {
    id: 'open-meadow',
    base: '#587a44',
    patch: '#7a6a45',
    accent: '#8d8a55',
    roughness: [0.82, 1],
    ...COMMON_SCALES,
  },
  // Emberwood Thicket (Tier 1 woodland): olive undergrowth, russet litter.
  'ember-woodland': {
    id: 'ember-woodland',
    base: '#4f5a34',
    patch: '#6a4a30',
    accent: '#7f6a3c',
    roughness: [0.82, 1],
    ...COMMON_SCALES,
  },
  // Iron Cleft (Tier 1 canyon): dusty ochre rock and grit, very little green.
  'canyon-grit': {
    id: 'canyon-grit',
    base: '#76604a',
    patch: '#5c4a3b',
    accent: '#91795c',
    roughness: [0.85, 1],
    ...COMMON_SCALES,
  },
  // Driftwell / Nimbus (Tier 0 towns): packed parchment-earth and worn footpath.
  'town-packed-earth': {
    id: 'town-packed-earth',
    base: '#7f7056',
    patch: '#66573f',
    accent: '#9a8b6c',
    roughness: [0.75, 0.95],
    ...COMMON_SCALES,
  },
  // Ashenveil / Whisperfen (Tier 2 swamp/wetland): cool, desaturated, damp mud.
  'damp-mire': {
    id: 'damp-mire',
    base: '#3a4235',
    patch: '#2f3430',
    accent: '#4c5442',
    roughness: [0.55, 0.9],
    ...COMMON_SCALES,
  },
  // Proving Grounds: an abstract neutral sparring flat (Bible §11 — not a place).
  'neutral-flat': {
    id: 'neutral-flat',
    base: '#6c6960',
    patch: '#5c5a53',
    accent: '#7c796f',
    roughness: [0.85, 1],
    ...COMMON_SCALES,
    grainAmount: 0.1,
  },
};

/**
 * The biome → palette table. A client-side PRESENTATION convention (like
 * `creatures/resolve.ts`), not content: when `content/themes/` gains a ground
 * block this table is what it replaces. A biome not listed gets no plan key.
 */
export const BIOME_GROUND_PALETTE: Readonly<Record<string, string>> = {
  'the-verdant-reach': 'verdant-forest',
  // The Measured Yard (Tier 1 training ground, AD-647): open, dry practice grass.
  'the-measured-yard': 'open-meadow',
  // The guest demo's echo of the Yard (Tier 2, AD-794): the same layout on the wet tier-2 ground
  // its fenroot and gloamspore grow in, so it reads as the other road, not the tutorial.
  'the-measured-yard-echo': 'damp-mire',
  'starlight-meadows': 'open-meadow',
  'emberwood-thicket': 'ember-woodland',
  'iron-cleft': 'canyon-grit',
  driftwell: 'town-packed-earth',
  'driftwell-plaza': 'town-packed-earth',
  nimbus: 'town-packed-earth',
  'ashenveil-depths': 'damp-mire',
  'whisperfen-lowlands': 'damp-mire',
  'proving-grounds': 'neutral-flat',
};

/** The palette id a biome binds, or `null` (⇒ no plan key, default palette). */
export function groundPaletteForBiome(biomeId: string): string | null {
  const id = BIOME_GROUND_PALETTE[biomeId];
  return id !== undefined && id in GROUND_PALETTES ? id : null;
}

/** Resolve a plan's optional palette key; unknown/absent ⇒ the default palette. */
export function resolveGroundPalette(id?: string | null): GroundPalette {
  const hit = id ? GROUND_PALETTES[id] : undefined;
  return hit ?? (GROUND_PALETTES[DEFAULT_GROUND_PALETTE_ID] as GroundPalette);
}

/** Parse `#rrggbb` to 0..1 sRGB channels. Throws on anything else. */
export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) throw new Error(`groundPalette: '${hex}' is not #rrggbb`);
  const n = Number.parseInt(m[1] as string, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Euclidean distance between two sRGB hex colours in 0..1 space (max √3). */
export function colourDistance(a: string, b: string): number {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return Math.hypot(ar - br, ag - bg, ab - bb);
}

/** The Bible §3.7 reserved combat hues a ground may never approach. */
export const RESERVED_COMBAT_HUES = ['#e8443a', '#39c46a'] as const;

/**
 * The value the shader's grain may push an albedo to, as a hex — used by tests
 * to prove the VARIED colour (not just the stops) stays clear of reserved hues.
 */
export function grainExtremes(palette: GroundPalette): string[] {
  const out: string[] = [];
  for (const hex of [palette.base, palette.patch, palette.accent]) {
    for (const k of [1 - palette.grainAmount, 1 + palette.grainAmount]) {
      const rgb = hexToRgb(hex).map((c) => Math.max(0, Math.min(1, c * k)));
      out.push(
        `#${rgb
          .map((c) =>
            Math.round(c * 255)
              .toString(16)
              .padStart(2, '0'),
          )
          .join('')}`,
      );
    }
  }
  return out;
}
